import React from 'react'
import ReactDOM from 'react-dom'
import { Link, useLocation } from 'react-router-dom'
import CapabilityGate from './CapabilityGate'
import useCapabilitiesStore from '../../stores/capabilitiesStore'
import { useTranslation } from 'react-i18next'
import {
  DropdownIcon,
  DropdownMenu,
  IconContainer,
  MenuButton,
  MenuItemWrapper,
  SectionContainer,
  SectionTitle,
  StyledSidebar,
  StyledTopSpacing,
  SubItemLink,
  SubItemsContainer,
  TooltipItem,
  Tooltip,
  DropdownSubItemLink,
  MenuItemBadge,
  ScrollList,
  NestedTooltip,
} from './sidebar-styles'
import { userContext } from '../../contexts/userContext'
import { useTheme } from '../../contexts/themeContext'
import { slugify } from '../../utils/stringUtilities'

const makeDropdownKey = (sectionLabel, itemLabel) => `${sectionLabel}::${itemLabel}`

const makeNestedKey = (sectionLabel, itemLabel, subLabel) =>
  `${sectionLabel}::${itemLabel}::${subLabel}`

const Sidebar = ({ isSidebarExpanded, menuItems }) => {
  const { i18n } = useTranslation()
  const {theme}=useTheme()
  const direction = i18n.language === 'ar' ? 'rtl' : 'ltr'
  const location = useLocation()
  const user = React.useContext(userContext)
  const [openDropdowns, setOpenDropdowns] = React.useState([])
  const [openNestedDropdowns, setOpenNestedDropdowns] = React.useState([])
  const [openDeepNestedDropdowns, setOpenDeepNestedDropdowns] = React.useState([])
  const [hoveredItem, setHoveredItem] = React.useState(null)
  const [hoveredNestedItem, setHoveredNestedItem] = React.useState(null)
  const [tooltipPosition, setTooltipPosition] = React.useState({
    left: 0,
    top: 0,
    isRTL: false,
  })

  const scrollListRef = React.useRef(null)
  const dropdownPanelRefs = React.useRef({})
  const nestedDropdownPanelRefs = React.useRef({})
  const pendingFocusKey = React.useRef(null)
  const pendingNestedFocusKey = React.useRef(null)

  const scrollPanelIntoSidebarView = React.useCallback((panelEl) => {
    const scroller = scrollListRef.current
    if (!scroller || !panelEl) return
    const scrollerRect = scroller.getBoundingClientRect()
    const panelRect = panelEl.getBoundingClientRect()
    const PADDING = 12
    if (panelRect.bottom > scrollerRect.bottom - PADDING) {
      const overshoot = panelRect.bottom - scrollerRect.bottom + PADDING
      scroller.scrollBy({ top: overshoot, behavior: 'smooth' })
    } else if (panelRect.top < scrollerRect.top + PADDING) {
      const overshoot = scrollerRect.top + PADDING - panelRect.top
      scroller.scrollBy({ top: -overshoot, behavior: 'smooth' })
    }
  }, [])

  const toggleDropdown = (key) => {
    setOpenDropdowns((prev) => {
      const isOpening = !prev.includes(key)
      if (isOpening) pendingFocusKey.current = key
      return isOpening ? [...prev, key] : prev.filter((k) => k !== key)
    })
  }

  const toggleNestedDropdown = (event, key) => {
    event.stopPropagation()
    setOpenNestedDropdowns((prev) => {
      const isOpening = !prev.includes(key)
      if (isOpening) pendingNestedFocusKey.current = key
      return isOpening ? [...prev, key] : prev.filter((k) => k !== key)
    })
  }

  const toggleDeepNestedDropdown = (event, key) => {
    event.stopPropagation()
    setOpenDeepNestedDropdowns((prev) => {
      const isOpening = !prev.includes(key)
      return isOpening ? [...prev, key] : prev.filter((k) => k !== key)
    })
  }

  React.useLayoutEffect(() => {
    if (!pendingFocusKey.current) return
    const key = pendingFocusKey.current
    pendingFocusKey.current = null
    const panel = dropdownPanelRefs.current[key]
    if (!panel) return
    scrollPanelIntoSidebarView(panel)
    const firstFocusable = panel.querySelector('a[href], button:not([disabled])')
    if (firstFocusable) firstFocusable.focus({ preventScroll: true })
  }, [openDropdowns, scrollPanelIntoSidebarView])

  React.useLayoutEffect(() => {
    if (!pendingNestedFocusKey.current) return
    const key = pendingNestedFocusKey.current
    pendingNestedFocusKey.current = null
    const panel = nestedDropdownPanelRefs.current[key]
    if (!panel) return
    scrollPanelIntoSidebarView(panel)
    const firstFocusable = panel.querySelector('a[href], button:not([disabled])')
    if (firstFocusable) firstFocusable.focus({ preventScroll: true })
  }, [openNestedDropdowns, scrollPanelIntoSidebarView])

  React.useEffect(() => {
    const parentKeys = []

    menuItems.forEach((section) => {
      section.items.forEach((item) => {
        if (item.subItems) {
          let shouldOpenParent = false
          const parentKey = makeDropdownKey(section.section, item.label)

          item.subItems.forEach((subItem) => {
            if (subItem.href === location.pathname) {
              shouldOpenParent = true
            }

            if (subItem.subItems) {
              subItem.subItems.forEach((nestedSubItem) => {
                if (nestedSubItem.href === location.pathname) {
                  shouldOpenParent = true
                  const nestedKey = makeNestedKey(section.section, item.label, subItem.label)
                  setOpenNestedDropdowns((prev) =>
                    prev.includes(nestedKey) ? prev : [...prev, nestedKey],
                  )
                }
              })
            }
          })

          if (shouldOpenParent && !parentKeys.includes(parentKey)) {
            parentKeys.push(parentKey)
          }
        }
      })
    })

    setOpenDropdowns(parentKeys)
  }, [location.pathname, menuItems])

  const shouldDisplaySection = (section) => {
    const visibleItems = section.items.filter((item) => {
      if (!user?.state?.isActive) return false
      if (!canDisplayMenuItem(item)) return false

      if (item.subItems) {
        const visibleSubItems = getVisibleSubItems(item.subItems)
        return visibleSubItems.length > 0
      }

      return true
    })

    return visibleItems.length > 0
  }

  const getVisibleSubItems = (subItems) => {
    if (!subItems) return []

    return subItems.filter((subItem) => {
      if (!canDisplayMenuItem(subItem)) return false

      if (subItem.subItems) {
        const visibleNestedSubItems = getVisibleSubItems(subItem.subItems)
        return visibleNestedSubItems.length > 0
      }

      return true
    })
  }

  const shouldDisplayDropdownItem = (item) => {
    if (!item.subItems) return true

    const visibleSubItems = getVisibleSubItems(item.subItems)
    return visibleSubItems.length > 0
  }

  const canDisplayMenuItem = (item) => {
    const store = useCapabilitiesStore.getState()
    if (item.permission && !store.hasPermission(item.permission)) return false
    if (item.feature && !store.isEnabled(item.feature)) return false
    return true
  }

  const hasVisibleNestedItems = (subItem) => {
    if (!subItem.subItems) return false

    return getVisibleSubItems(subItem.subItems).length > 0
  }

  return (
    <ScrollList ref={scrollListRef}>
      <StyledSidebar dir={direction}>
        <StyledTopSpacing />

        {menuItems.map((section) => {
          if (!shouldDisplaySection(section)) return null

          return (
            <SectionContainer key={section.section}>
              {isSidebarExpanded && (
                <SectionTitle>{section.section}</SectionTitle>
              )}

              {section.items.map((item) => {
                const hasPermission = item.permission !== undefined
                const hasFeature = item.feature !== undefined
                const ItemWrapper = hasPermission || hasFeature
                  ? CapabilityGate
                  : React.Fragment
                const wrapperProps = {
                  ...(hasPermission ? { permission: item.permission } : {}),
                  ...(hasFeature ? { feature: item.feature } : {}),
                }

                const isActive =
                  item.href && !item.subItems && location.pathname === item.href

                const isSubActive = item.subItems
                  ? item.subItems.some((subItem) => {
                      if (location.pathname === subItem.href) return true

                      if (subItem.subItems) {
                        return subItem.subItems.some((nestedSubItem) => {
                          if (location.pathname === nestedSubItem.href) return true
                          if (nestedSubItem.subItems) {
                            return nestedSubItem.subItems.some(
                              (deepItem) => location.pathname === deepItem.href,
                            )
                          }
                          return false
                        })
                      }

                      return false
                    })
                  : false

                if (!canDisplayMenuItem(item))
                  return null

                if (item.subItems && !shouldDisplayDropdownItem(item))
                  return null

                const dropdownKey = makeDropdownKey(section.section, item.label)
                const panelSlug = slugify(dropdownKey)

                return (
                  <ItemWrapper key={item.label} {...wrapperProps}>
                    <div>
                      <MenuItemWrapper
                        onMouseEnter={(e) => {
                          if (!isSidebarExpanded) {
                            const rect = e.currentTarget.getBoundingClientRect()
                            const isRTL = direction === 'rtl'

                            setHoveredItem(item.label)
                            setTooltipPosition({
                              left: isRTL
                                ? rect.left - 160 - 4
                                : rect.right + 4,
                              top: rect.top,
                              isRTL,
                            })
                          }
                        }}
                        onMouseLeave={() => setHoveredItem(null)}
                      >
                        <MenuButton
                          active={isActive || isSubActive}
                          as={item.href && !item.subItems ? Link : 'button'}
                          to={
                            item.href && !item.subItems ? item.href : undefined
                          }
                          onClick={() =>
                            item.subItems
                              ? toggleDropdown(dropdownKey)
                              : undefined
                          }
                          isExpanded={isSidebarExpanded}
                          aria-expanded={item.subItems ? openDropdowns.includes(dropdownKey) : undefined}
                          aria-controls={item.subItems ? `dp-${panelSlug}` : undefined}
                        >
                          <IconContainer isExpanded={isSidebarExpanded}>
                            <item.icon
                              width={16}
                              height={16}
                              style={{
                                marginRight: direction === 'rtl' ? 0 : '8.7px',
                                marginLeft: direction === 'rtl' ? '8.7px' : 0,
                              }}
                            />
                            <span>{item.label}</span>
                          </IconContainer>

                          {isSidebarExpanded && item.subItems && (
                            <DropdownIcon
                              width={12}
                              height={12}
                              $isOpen={openDropdowns.includes(dropdownKey)}
                            />
                          )}

                          {isSidebarExpanded && item.count && (
                            <MenuItemBadge
                              borderColor={item.count.borderColor}
                              backgroundColor={item.count.backgroundColor}
                              className={item.count.className}
                              hoverText={item.count.hoverText}
                              style={{
                                marginLeft: direction === 'rtl' ? '0' : 'auto',
                                marginRight: direction === 'rtl' ? 'auto' : '0',
                                transform: 'none',
                              }}
                            >
                              {item.count.value}
                            </MenuItemBadge>
                          )}
                        </MenuButton>

                        {!isSidebarExpanded &&
                          hoveredItem === item.label &&
                          ReactDOM.createPortal(
                            <Tooltip
                              style={{
                                left: tooltipPosition.left,
                                top: tooltipPosition.top,
                                transformOrigin: tooltipPosition.isRTL
                                  ? 'right'
                                  : 'left',
                              }}
                              dir={direction}
                            >
                              <TooltipItem>
                                <div
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    width: '100%',
                                  }}
                                >
                                  <span>{item.label}</span>
                                  {item.count && (
                                    <MenuItemBadge
                                      borderColor={item.count.borderColor}
                                      backgroundColor={
                                        item.count.backgroundColor
                                      }
                                      className={item.count.className}
                                      hoverText={item.count.hoverText}
                                      style={{
                                        position: 'relative',
                                        transform: 'none',
                                      }}
                                    >
                                      {item.count.value}
                                    </MenuItemBadge>
                                  )}
                                </div>
                              </TooltipItem>
                              {item.subItems && (
                                <SubItemsContainer>
                                  {item.subItems.map((subItem) => {
                                    if (
                                      subItem.permission &&
                                      !useCapabilitiesStore.getState().hasPermission(subItem.permission)
                                    )
                                      return null

                                    const hasNestedItems =
                                      hasVisibleNestedItems(subItem)

                                    return (
                                      <CapabilityGate
                                        key={subItem.label}
                                        permission={subItem.permission}
                                      >
                                        <div
                                          style={{ position: 'relative' }}
                                          onMouseEnter={() =>
                                            hasNestedItems &&
                                            setHoveredNestedItem(subItem.label)
                                          }
                                          onMouseLeave={() =>
                                            setHoveredNestedItem(null)
                                          }
                                        >
                                          {hasNestedItems ? (
                                            <div
                                              className="nested-item-container"
                                              style={{
                                                display: 'flex',
                                                padding: '6px 12px',
                                                fontSize: '12px',
                                                fontWeight: 600,
                                                transition:
                                                  'background-color 200ms',
                                                color: 'inherit',
                                                backgroundColor: 'transparent',
                                                cursor: 'pointer',
                                              }}
                                            >
                                              <div
                                                style={{
                                                  display: 'flex',
                                                  justifyContent:
                                                    'space-between',
                                                  alignItems: 'center',
                                                  gap: '8px',
                                                  width: '100%',
                                                }}
                                              >
                                                <span>{subItem.label}</span>
                                                <DropdownIcon
                                                  width={10}
                                                  height={10}
                                                  $isOpen={
                                                    hoveredNestedItem ===
                                                    subItem.label
                                                  }
                                                  style={{ marginLeft: '8px' }}
                                                />
                                                {subItem.count && (
                                                  <MenuItemBadge
                                                    borderColor={
                                                      subItem.count.borderColor
                                                    }
                                                    backgroundColor={
                                                      subItem.count
                                                        .backgroundColor
                                                    }
                                                    className={
                                                      subItem.count.className
                                                    }
                                                    hoverText={
                                                      subItem.count.hoverText
                                                    }
                                                    style={{
                                                      position: 'relative',
                                                      transform: 'none',
                                                    }}
                                                  >
                                                    {subItem.count.value}
                                                  </MenuItemBadge>
                                                )}
                                              </div>
                                            </div>
                                          ) : (
                                            <SubItemLink
                                              to={subItem.href}
                                              active={
                                                location.pathname ===
                                                subItem.href
                                              }
                                            >
                                              <div
                                                style={{
                                                  display: 'flex',
                                                  justifyContent:
                                                    'space-between',
                                                  alignItems: 'center',
                                                  gap: '8px',
                                                  width: '100%',
                                                }}
                                              >
                                                <span>{subItem.label}</span>
                                                {subItem.count && (
                                                  <MenuItemBadge
                                                    borderColor={
                                                      subItem.count.borderColor
                                                    }
                                                    backgroundColor={
                                                      subItem.count
                                                        .backgroundColor
                                                    }
                                                    className={
                                                      subItem.count.className
                                                    }
                                                    hoverText={
                                                      subItem.count.hoverText
                                                    }
                                                    style={{
                                                      position: 'relative',
                                                      transform: 'none',
                                                    }}
                                                  >
                                                    {subItem.count.value}
                                                  </MenuItemBadge>
                                                )}
                                              </div>
                                            </SubItemLink>
                                          )}

                                          {hasNestedItems &&
                                            hoveredNestedItem ===
                                              subItem.label && (
                                              <NestedTooltip>
                                                <SubItemsContainer>
                                                  {subItem.subItems.map(
                                                    (nestedSubItem) => {
                                                      if (
                                                        nestedSubItem.permission &&
                                                        !useCapabilitiesStore.getState().hasPermission(
                                                          nestedSubItem.permission,
                                                        )
                                                      )
                                                        return null

                                                      return (
                                                        <CapabilityGate
                                                          key={
                                                            nestedSubItem.label
                                                          }
                                                          permission={
                                                            nestedSubItem.permission
                                                          }
                                                        >
                                                          <SubItemLink
                                                            to={
                                                              nestedSubItem.href
                                                            }
                                                            active={
                                                              location.pathname ===
                                                              nestedSubItem.href
                                                            }
                                                          >
                                                            <div
                                                              style={{
                                                                display: 'flex',
                                                                justifyContent:
                                                                  'space-between',
                                                                alignItems:
                                                                  'center',
                                                                gap: '8px',
                                                                width: '100%',
                                                              }}
                                                            >
                                                              <span>
                                                                {
                                                                  nestedSubItem.label
                                                                }
                                                              </span>
                                                              {nestedSubItem.count && (
                                                                <MenuItemBadge
                                                                  borderColor={
                                                                    nestedSubItem
                                                                      .count
                                                                      .borderColor
                                                                  }
                                                                  backgroundColor={
                                                                    nestedSubItem
                                                                      .count
                                                                      .backgroundColor
                                                                  }
                                                                  className={
                                                                    nestedSubItem
                                                                      .count
                                                                      .className
                                                                  }
                                                                  hoverText={
                                                                    nestedSubItem
                                                                      .count
                                                                      .hoverText
                                                                  }
                                                                  style={{
                                                                    position:
                                                                      'relative',
                                                                    transform:
                                                                      'none',
                                                                  }}
                                                                >
                                                                  {
                                                                    nestedSubItem
                                                                      .count
                                                                      .value
                                                                  }
                                                                </MenuItemBadge>
                                                              )}
                                                            </div>
                                                          </SubItemLink>
                                                        </CapabilityGate>
                                                      )
                                                    },
                                                  )}
                                                </SubItemsContainer>
                                              </NestedTooltip>
                                            )}
                                        </div>
                                      </CapabilityGate>
                                    )
                                  })}
                                </SubItemsContainer>
                              )}
                            </Tooltip>,
                            document.body,
                          )}
                      </MenuItemWrapper>

                      {item.subItems &&
                        openDropdowns.includes(dropdownKey) &&
                        isSidebarExpanded && (
                          <DropdownMenu
                            id={`dp-${panelSlug}`}
                            ref={(el) => { dropdownPanelRefs.current[dropdownKey] = el }}
                          >
                            {item.subItems.map((subItem) => {
                              if (
                                subItem.permission &&
                                !useCapabilitiesStore.getState().hasPermission(subItem.permission)
                              )
                                return null

                              const hasNestedItems =
                                hasVisibleNestedItems(subItem)

                              const isNestedActive =
                                hasNestedItems &&
                                subItem.subItems.some(
                                  (nestedItem) =>
                                    location.pathname === nestedItem.href,
                                )

                              const nestedKey = makeNestedKey(section.section, item.label, subItem.label)
                              const nestedSlug = slugify(nestedKey)

                              return (
                                <CapabilityGate
                                  key={subItem.label}
                                  permission={subItem.permission}
                                >
                                  <div>
                                    {hasNestedItems ? (
                                      <button
                                        className={`dropdown-sub-item ${location.pathname === subItem.href || isNestedActive ? 'active' : ''}`}
                                        onClick={(e) =>
                                          toggleNestedDropdown(e, nestedKey)
                                        }
                                        aria-expanded={openNestedDropdowns.includes(nestedKey)}
                                        aria-controls={`np-${nestedSlug}`}
                                        style={{
                                          width: '100%',
                                          textAlign: 'left',
                                          background:
                                            location.pathname ===
                                              subItem.href || isNestedActive
                                              ? theme.sidebar
                                              : 'transparent',
                                          border: 'none',
                                          padding: '6px 12px',
                                          fontSize: '12px',
                                          fontWeight: 600,
                                          borderRadius: '6px',
                                          transition: 'background-color 200ms',
                                          color: theme.sidebarTextColor,
                                          cursor: 'pointer',
                                        }}
                                      >
                                        <div
                                          style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            width: '100%',
                                          }}
                                        >
                                          <span>{subItem.label}</span>
                                          <DropdownIcon
                                            width={10}
                                            height={10}
                                            $isOpen={openNestedDropdowns.includes(nestedKey)}
                                            style={{ marginLeft: '8px' }}
                                          />
                                          {subItem.count && (
                                            <MenuItemBadge
                                              borderColor={
                                                subItem.count.borderColor
                                              }
                                              backgroundColor={
                                                subItem.count.backgroundColor
                                              }
                                              className={
                                                subItem.count.className
                                              }
                                              hoverText={
                                                subItem.count.hoverText
                                              }
                                              style={{
                                                position: 'relative',
                                                transform: 'none',
                                              }}
                                            >
                                              {subItem.count.value}
                                            </MenuItemBadge>
                                          )}
                                        </div>
                                      </button>
                                    ) : (
                                      <DropdownSubItemLink
                                        to={subItem.href}
                                        active={
                                          location.pathname === subItem.href
                                        }
                                      >
                                        <div
                                          style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            width: '100%',
                                          }}
                                        >
                                          <span>{subItem.label}</span>
                                          {subItem.count && (
                                            <MenuItemBadge
                                              borderColor={
                                                subItem.count.borderColor
                                              }
                                              backgroundColor={
                                                subItem.count.backgroundColor
                                              }
                                              className={
                                                subItem.count.className
                                              }
                                              hoverText={
                                                subItem.count.hoverText
                                              }
                                              style={{
                                                position: 'relative',
                                                transform: 'none',
                                              }}
                                            >
                                              {subItem.count.value}
                                            </MenuItemBadge>
                                          )}
                                        </div>
                                      </DropdownSubItemLink>
                                    )}

                                    {hasNestedItems &&
                                      openNestedDropdowns.includes(nestedKey) && (
                                        <DropdownMenu
                                          id={`np-${nestedSlug}`}
                                          ref={(el) => { nestedDropdownPanelRefs.current[nestedKey] = el }}
                                          style={{
                                            marginLeft:
                                              direction === 'rtl' ? '0' : '7px',
                                            marginRight:
                                              direction === 'rtl' ? '7px' : '0',
                                          }}
                                        >
                                          {subItem.subItems.map(
                                            (nestedSubItem) => {
                                              if (
                                                nestedSubItem.permission &&
                                                !useCapabilitiesStore.getState().hasPermission(
                                                  nestedSubItem.permission,
                                                )
                                              )
                                                return null

                                              const hasDeepNestedItems =
                                                nestedSubItem.subItems &&
                                                getVisibleSubItems(nestedSubItem.subItems).length > 0

                                              const isDeepNestedActive =
                                                hasDeepNestedItems &&
                                                nestedSubItem.subItems.some(
                                                  (d) => location.pathname === d.href,
                                                )

                                              return (
                                                <CapabilityGate
                                                  key={nestedSubItem.label}
                                                  permission={nestedSubItem.permission}
                                                >
                                                  <div>
                                                    {hasDeepNestedItems ? (
                                                      <button
                                                        className={`dropdown-sub-item ${isDeepNestedActive ? 'active' : ''}`}
                                                        onClick={(e) =>
                                                          toggleDeepNestedDropdown(e, nestedSubItem.label)
                                                        }
                                                        style={{
                                                          width: '100%',
                                                          textAlign: 'left',
                                                          background: isDeepNestedActive
                                                            ? theme.sidebar
                                                            : 'transparent',
                                                          border: 'none',
                                                          padding: '6px 12px',
                                                          fontSize: '12px',
                                                          fontWeight: 600,
                                                          borderRadius: '6px',
                                                          transition: 'background-color 200ms',
                                                          color: theme.sidebarTextColor,
                                                          cursor: 'pointer',
                                                        }}
                                                      >
                                                        <div
                                                          style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            width: '100%',
                                                          }}
                                                        >
                                                          <span>{nestedSubItem.label}</span>
                                                          <DropdownIcon
                                                            width={10}
                                                            height={10}
                                                            $isOpen={openDeepNestedDropdowns.includes(
                                                              nestedSubItem.label,
                                                            )}
                                                            style={{ marginLeft: '8px' }}
                                                          />
                                                        </div>
                                                      </button>
                                                    ) : (
                                                      <DropdownSubItemLink
                                                        to={nestedSubItem.href}
                                                        active={location.pathname === nestedSubItem.href}
                                                      >
                                                        <div
                                                          style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            width: '100%',
                                                          }}
                                                        >
                                                          <span>{nestedSubItem.label}</span>
                                                        </div>
                                                      </DropdownSubItemLink>
                                                    )}

                                                    {hasDeepNestedItems &&
                                                      openDeepNestedDropdowns.includes(nestedSubItem.label) && (
                                                        <DropdownMenu
                                                          style={{
                                                            marginLeft: direction === 'rtl' ? '0' : '7px',
                                                            marginRight: direction === 'rtl' ? '7px' : '0',
                                                          }}
                                                        >
                                                          {nestedSubItem.subItems.map((deepItem) => {
                                                            if (
                                                              deepItem.permission &&
                                                              !useCapabilitiesStore.getState().hasPermission(deepItem.permission)
                                                            )
                                                              return null

                                                            return (
                                                              <CapabilityGate
                                                                key={deepItem.label}
                                                                permission={deepItem.permission}
                                                              >
                                                                <DropdownSubItemLink
                                                                  to={deepItem.href}
                                                                  active={location.pathname === deepItem.href}
                                                                >
                                                                  <div
                                                                    style={{
                                                                      display: 'flex',
                                                                      justifyContent: 'space-between',
                                                                      alignItems: 'center',
                                                                      width: '100%',
                                                                    }}
                                                                  >
                                                                    <span>{deepItem.label}</span>
                                                                  </div>
                                                                </DropdownSubItemLink>
                                                              </CapabilityGate>
                                                            )
                                                          })}
                                                        </DropdownMenu>
                                                      )}
                                                  </div>
                                                </CapabilityGate>
                                              )
                                            },
                                          )}
                                        </DropdownMenu>
                                      )}
                                  </div>
                                </CapabilityGate>
                              )
                            })}
                          </DropdownMenu>
                        )}
                    </div>
                  </ItemWrapper>
                )
              })}
            </SectionContainer>
          )
        })}
      </StyledSidebar>
    </ScrollList>
  )
}

export default Sidebar
